import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ApplicationButton,
  ApplicationPage,
  ApplicationStateRegion,
  ApplicationSurface,
  ApplicationViewHeader,
} from "../../../components/application";
import AssignmentActivityChooser from "../components/AssignmentActivityChooser";
import {
  AssignmentDatesSection,
  AssignmentDetailsSection,
  AssignmentPeopleSection,
  AssignmentReview,
  splitAssignmentEmails,
} from "../components/AssignmentCompositionSections";
import {
  createAssignment,
  getAssignmentCompositionActions,
  inspectAssignmentActivity,
  listLaunchableEvaluationTasks,
} from "../services/assignmentApi";
import {
  consumeAssignmentCompositionReturn,
  readAssignmentCompositionReturn,
  saveAssignmentCompositionReturn,
} from "../services/assignmentCompositionRecovery";

const INITIAL_FORM = {
  title: "",
  description: "",
  instructions: "",
  assigneeEmails: "",
  availableFrom: "",
  dueAt: "",
  closesAt: "",
};

function normalizeDateTimeLocal(value) {
  return value ? new Date(value).toISOString() : undefined;
}

export default function CreateAssignmentPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnToken = searchParams.get("returnToken");
  const returnedActivityId = searchParams.get("activityId");
  const [activities, setActivities] = useState([]);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [changingActivity, setChangingActivity] = useState(false);
  const [form, setForm] = useState(INITIAL_FORM);
  const [loadingActivities, setLoadingActivities] = useState(true);
  const [activityError, setActivityError] = useState("");
  const [inspection, setInspection] = useState(null);
  const [inspectionOpen, setInspectionOpen] = useState(false);
  const [inspectionLoading, setInspectionLoading] = useState(false);
  const [inspectionError, setInspectionError] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [validActions, setValidActions] = useState({ chooseActivity: false, createActivity: false });
  const [returnState, setReturnState] = useState({ state: returnToken ? "pending" : "idle", message: "" });

  const loadActivities = useCallback(async () => {
    try {
      setLoadingActivities(true);
      const [loadedActivities, actions] = await Promise.all([
        listLaunchableEvaluationTasks(),
        getAssignmentCompositionActions(),
      ]);
      setActivities(loadedActivities);
      setValidActions(actions);
      setActivityError("");
    } catch {
      setActivityError("Activities could not be loaded. Please try again.");
    } finally {
      setLoadingActivities(false);
    }
  }, []);

  useEffect(() => { loadActivities(); }, [loadActivities]);

  const restoreReturn = useCallback(async () => {
    if (!returnToken) return;
    const recovered = readAssignmentCompositionReturn(returnToken);
    if (recovered.state !== "valid") {
      setSelectedActivity(null);
      setReturnState({ state: "failed", message: recovered.state === "invalid"
        ? "The saved Assignment composition was invalid and could not be restored."
        : "This Assignment return could not be restored. Choose an activity to continue." });
      return;
    }
    setForm(recovered.payload.form);
    if (!returnedActivityId) {
      setSelectedActivity(activities.find(activity => activity.id === recovered.payload.priorActivityId) || null);
      setChangingActivity(false);
      consumeAssignmentCompositionReturn(returnToken);
      navigate("/app/assignments/new", { replace: true });
      setReturnState({ state: "restored", message: "Your Assignment composition was restored." });
      return;
    }
    try {
      setReturnState({ state: "resolving", message: "Checking the new activity…" });
      const resolved = await inspectAssignmentActivity(returnedActivityId);
      setSelectedActivity({
        id: resolved.id,
        name: resolved.name,
        provenance: resolved.visibility === "only_you" ? "my_activity" : "provided_by_allvoiced",
        version: resolved.lineage?.version,
        family: resolved.authoringMethod?.key === "cape_vr" ? "cape_vr" : "perceptual_activity",
        canInspect: true,
      });
      setChangingActivity(false);
      consumeAssignmentCompositionReturn(returnToken);
      navigate("/app/assignments/new", { replace: true });
      setReturnState({ state: "restored", message: "Your Assignment composition was restored and the new ready activity was selected." });
    } catch (resolutionError) {
      const status = resolutionError?.response?.status;
      if (!status || status >= 500 || status === 408 || status === 429) {
        setReturnState({ state: "retry", message: "The new activity could not be checked right now. Your Assignment composition is still safe." });
        return;
      }
      setSelectedActivity(null);
      setChangingActivity(false);
      consumeAssignmentCompositionReturn(returnToken);
      navigate("/app/assignments/new", { replace: true });
      setReturnState({ state: "failed", message: "The new activity cannot currently be assigned. Choose another activity to continue." });
    }
  }, [activities, navigate, returnToken, returnedActivityId]);

  useEffect(() => {
    if (returnToken && !loadingActivities && !activityError && returnState.state === "pending") restoreReturn();
  }, [activityError, loadingActivities, restoreReturn, returnState.state, returnToken]);

  function updateField(event) {
    const { name, value } = event.target;
    setForm(current => ({ ...current, [name]: value }));
  }

  async function viewActivity(activity) {
    try {
      setInspectionOpen(true);
      setInspectionLoading(true);
      setInspectionError("");
      setInspection(await inspectAssignmentActivity(activity.id));
    } catch {
      setInspection(null);
      setInspectionError("This activity could not be inspected. Please try again.");
    } finally {
      setInspectionLoading(false);
    }
  }

  async function handleCreate(event) {
    event.preventDefault();
    if (!selectedActivity) {
      setError("Choose an activity before creating the assignment draft.");
      return;
    }
    try {
      setSaving(true);
      const created = await createAssignment({
        title: form.title,
        description: form.description,
        instructions: form.instructions,
        evaluationTaskId: selectedActivity.id,
        assigneeEmails: splitAssignmentEmails(form.assigneeEmails),
        availableFrom: normalizeDateTimeLocal(form.availableFrom),
        dueAt: normalizeDateTimeLocal(form.dueAt),
        closesAt: normalizeDateTimeLocal(form.closesAt),
      });
      navigate(`/app/assignments/${created._id}`);
    } catch (createError) {
      const code = createError?.response?.data?.error;
      const messages = {
        ASSIGNEE_NOT_FOUND: "One or more email addresses do not belong to an existing All-Voiced user. Check People and try again.",
        INVALID_DATE: "One or more dates are invalid. Check Dates and try again.",
        INVALID_DATE_ORDER: "Check Dates: Available from must precede Due and Closes, and Due must precede Closes.",
        TITLE_REQUIRED: "Add an Assignment title before creating the draft.",
        EVALUATION_TASK_NOT_SELECTABLE_FOR_ASSIGNMENT: "The selected Activity is no longer available for a new Assignment. Choose another Activity.",
      };
      setError(messages[code] || "Assignment draft could not be created. Your composition is still here; check the highlighted sections and try again.");
    } finally {
      setSaving(false);
    }
  }

  return <ApplicationPage variant="workspace">
    <ApplicationViewHeader
      contextLabel="Assignments"
      title="Create assignment"
      description="Choose a ready activity, then add the current Assignment details. Publishing remains a separate step."
      backNavigation={<ApplicationButton variant="quiet" onClick={() => navigate("/app/assignments")}>Back to assignments</ApplicationButton>}
    />
    {error ? <ApplicationStateRegion status="error" message={error} /> : null}
    {returnState.message ? <><ApplicationStateRegion status={returnState.state === "failed" || returnState.state === "retry" ? "error" : "information"} message={returnState.message} />{returnState.state === "retry" ? <ApplicationButton variant="secondary" onClick={restoreReturn}>Retry activity check</ApplicationButton> : null}</> : null}
    <form onSubmit={handleCreate} className="av-assignment-composition">
    <ApplicationSurface id="assignment-activity">
      <h2>Activity</h2>
      {loadingActivities ? <ApplicationStateRegion status="loading" message="Loading activities…" /> : null}
      {activityError ? <><ApplicationStateRegion status="error" message={activityError} /><ApplicationButton variant="secondary" onClick={loadActivities}>Retry</ApplicationButton></> : null}
      {!loadingActivities && !activityError ? <AssignmentActivityChooser
        activities={activities}
        selectedActivity={selectedActivity}
        inspection={inspection}
        inspectionLoading={inspectionLoading}
        inspectionError={inspectionError}
        inspectionOpen={inspectionOpen}
        onChoose={(activity) => { setSelectedActivity(activity); setChangingActivity(false); setInspection(null); setInspectionError(""); }}
        onChange={() => { setChangingActivity(true); setInspection(null); setInspectionError(""); }}
        changing={changingActivity}
        onCancelChange={() => setChangingActivity(false)}
        onView={viewActivity}
        onCloseInspection={() => { setInspectionOpen(false); setInspection(null); setInspectionError(""); }}
        canCreateActivity={validActions.createActivity === true}
        onCreateActivity={() => {
          const token = saveAssignmentCompositionReturn({ form, selectedActivity });
          navigate(`/app/facilitate/activities/new?returnToken=${encodeURIComponent(token)}`);
        }}
      /> : null}
    </ApplicationSurface>
    <AssignmentPeopleSection form={form} onChange={updateField} />
    <AssignmentDatesSection form={form} onChange={updateField} />
    <AssignmentDetailsSection form={form} onChange={updateField} />
    <AssignmentReview form={form} selectedActivity={selectedActivity} onEdit={(section) => section === "activity" && setChangingActivity(Boolean(selectedActivity))} onViewActivity={viewActivity} />
    <div className="av-assignment-composition__submit"><p>Creating saves a draft. Publishing remains a separate action on the Assignment detail page.</p><ApplicationButton disabled={saving} type="submit">{saving ? "Creating…" : "Create draft"}</ApplicationButton></div>
    </form>
  </ApplicationPage>;
}

export { INITIAL_FORM, normalizeDateTimeLocal, splitAssignmentEmails };
